'use client'
import React, {useEffect, useRef, useState} from 'react'
import {cx} from '@local/utils/cx'

type ItemType = {
	label: string;
	href: string;
};

type AnchorTabProps = {
	items: ItemType[];
};

type AnchorTabItemProps = ItemType & {
	active: boolean;
	onClick: () => void;
};

export const AnchorTab = ({items}: AnchorTabProps) => {
	// State management
	const [activeItem, setActiveItem] = useState<string>('')
	const [isHidden, setIsHidden] = useState(false)
	const [isOpen, setIsOpen] = useState(false)
	const [isAnyModalOpen, setIsAnyModalOpen] = useState(false)
	const [isMobile, setIsMobile] = useState(false)
	const [validItems, setValidItems] = useState<ItemType[]>([])

	// Refs for observers and timers
	const observer = useRef<IntersectionObserver | null>(null)
	const heroObserver = useRef<IntersectionObserver | null>(null)
	const lastScrollY = useRef<number>(0)
	const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
	const activeChangeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Filter valid items on mount and when items change
	useEffect(() => {
		const validTabs = items.filter(item => {
			const element = document.getElementById(item.href)
			return element !== null
		})
		setValidItems(validTabs)

		// Set initial active item to first valid tab
		if (validTabs.length > 0 && !activeItem) {
			setActiveItem(validTabs[0].href)
		}
	}, [items])

	// Check for mobile/desktop view
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 1024) // lg breakpoint
		}

		// Initial check
		checkMobile()

		// Listen for resize events
		window.addEventListener('resize', checkMobile)

		return () => {
			window.removeEventListener('resize', checkMobile)
		}
	}, [])

	// Monitor DOM for open slideouts/modals
	useEffect(() => {
		const checkForOpenModals = () => {
			const openSlideout = document.querySelector('[data-slideout-open="true"]')
			setIsAnyModalOpen(!!openSlideout)

			// Auto-close dropdown when modal opens
			if (!!openSlideout && isOpen) {
				setIsOpen(false)
			}
		}

		checkForOpenModals()

		const observer = new MutationObserver(checkForOpenModals)
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-slideout-open', 'class'],
		})

		return () => observer.disconnect()
	}, [isOpen])

	// Intersection observer setup
	useEffect(() => {
		const handleHeroIntersection = (entries: IntersectionObserverEntry[]) => {
			const heroEntry = entries.find((entry) => entry.isIntersecting)
			if (heroEntry && heroEntry.intersectionRatio > 0.3) {
				// Only set to the first item if hero is significantly visible
				setActiveItem(validItems[0].href)
				setIsHidden(true)
			}
		}

		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			// Find all visible sections
			const visibleEntries = entries.filter(entry => entry.isIntersecting)

			if (visibleEntries.length > 0) {
				// Find the entry with highest intersection ratio (most visible)
				const mostVisibleEntry = visibleEntries.reduce((prev, current) =>
					current.intersectionRatio > prev.intersectionRatio ? current : prev,
				)

				// Use a longer timeout to prevent rapid state changes and flickering
				if (activeChangeTimeout.current) clearTimeout(activeChangeTimeout.current)
				activeChangeTimeout.current = setTimeout(() => {
					setActiveItem(mostVisibleEntry.target.id)
				}, 200)
			}
		}

		heroObserver.current = new IntersectionObserver(handleHeroIntersection, {
			root: null,
			rootMargin: '0px',
			threshold: 0,
		})

		observer.current = new IntersectionObserver(handleIntersection, {
			root: null,
			rootMargin: '-100px 0px -20% 0px',
			threshold: [0.1, 0.2, 0.3, 0.4, 0.5],
		})

		const heroElement = document.getElementById('hero')

		validItems.forEach((item) => {
			const element = document.getElementById(item.href)
			if (element) observer.current?.observe(element)
		})

		if (heroElement) heroObserver.current?.observe(heroElement)

		return () => {
			observer.current?.disconnect()
			heroObserver.current?.disconnect()
			if (activeChangeTimeout.current) clearTimeout(activeChangeTimeout.current)
		}
	}, [validItems])

	useEffect(() => {
		// Make sure menu is visible on initial load and when at page top
		if (window.scrollY === 0) {
			setIsHidden(false)
		}

		const handleScroll = () => {
			const currentScrollY = window.scrollY
			const scrollDifference = Math.abs(currentScrollY - lastScrollY.current)

			// When at the top of the page, always show menu
			if (currentScrollY === 0) {
				setIsHidden(false)
				return
			}

			// Different behavior for mobile and desktop
			if (isMobile) {
				// For mobile: only hide when actually scrolling and dropdown is not being opened
				if (scrollDifference > 20 && !isOpen) {
					setIsHidden(true)
					lastScrollY.current = currentScrollY

					// Close dropdown on scroll
					if (isOpen) {
						setIsOpen(false)
					}
				}
			} else {
				// For desktop: only hide when scrolling down significantly
				if (scrollDifference > 50) {
					setIsHidden(currentScrollY > lastScrollY.current && currentScrollY > 150)
					lastScrollY.current = currentScrollY

					// Close dropdown on significant scroll
					if (isOpen) {
						setIsOpen(false)
					}
				}
			}

			// Reset the timer on each scroll event
			if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
			scrollTimeout.current = setTimeout(() => {
				setIsHidden(false)
			}, isMobile ? 500 : 800) // Faster reappearance on mobile
		}

		window.addEventListener('scroll', handleScroll, {passive: true})
		return () => window.removeEventListener('scroll', handleScroll)
	}, [isOpen, isMobile])

	// Ensure menu is visible on initial load
	useEffect(() => {
		setIsHidden(false)
	}, [])

	// Navigation handler
	const onClick = (href: string) => {
		const element = document.getElementById(href)
		if (element) {
			const headerOffset = 120
			const elementPosition = window.scrollY + element.getBoundingClientRect().top - headerOffset

			window.scrollTo({
				top: elementPosition,
				behavior: 'smooth',
			})

			setTimeout(() => {
				setActiveItem(href)
			}, 100)
		}
	}

	const toggleDropdown = () => {
		setIsOpen(!isOpen)
	}

	return (
		<div className={cx(
			'sticky top-60 md:top-0 w-full z-1 transition-all duration-300 ease-in-out',
			isAnyModalOpen && '-z-1',
		)}>
			{/* Desktop tabs */}
			<div className={cx(
				'hidden lg:flex flex-row items-center justify-center bg-white-off gap-x-10 pt-70 pb-10',
				'transition-opacity duration-300',
			)}>
				{validItems.map((item) => (
					<AnchorTabItem
						key={item.href}
						label={item.label}
						href={item.href}
						active={activeItem === item.href}
						onClick={() => onClick(item.href)} />
				))}
			</div>

			<div className={cx(
				'lg:hidden left-0 right-0 px-10 pt-10 z-[999] transition-all duration-300 ease-in-out',
				isHidden ? 'opacity-0 transform -translate-y-10' : 'opacity-100 transform translate-y-0',
			)}>

				<div
					className={cx(
						'flex items-center justify-between h-44 px-16 border rounded-sm cursor-pointer bg-white',
						'transition-all duration-300 ease-in-out',
						isOpen && 'rounded-b-none',
					)}
					onClick={toggleDropdown}>

					<p className='mr-2 text-14 font-[350] text-black transition-colors leading-none duration-300'>
						{validItems.find(item => item.href === activeItem)?.label}
					</p>
					<svg
						className={cx(
							'w-12 h-12 transition-transform duration-300 ease-in-out',
							isOpen ? 'rotate-180' : '',
						)}
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'>
						<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
					</svg>
				</div>

				{/* Backdrop and dropdown content with animations */}
				{isOpen && !isAnyModalOpen && (
					<>
						{/* Backdrop with fade in/out */}
						<div
							className=' inset-0 bg-black z-30  animate-fadeIn'
							style={{
								opacity: 0.3,
								transition: 'opacity 300ms ease-in-out',
							}}
							onClick={() => setIsOpen(false)} />

						{/* Dropdown content with slide down animation */}
						<div
							className='border border-t-0 rounded-b-md bg-white shadow-lg relative z-40 max-h-[70vh] overflow-y-auto animate-slideDown'
							style={{
								transformOrigin: 'top center',
								animation: 'slideDown 300ms ease-out forwards',
							}}>
							{validItems.map((item) => (
								<div
									key={item.href}
									className={cx(
										'p-4 border-b last:border-b-0 hover:bg-gray-50',
										'transition-all duration-200 ease-in-out',
										activeItem === item.href ? 'bg-gray-100 font-medium' : '',
									)}
									onClick={() => {
										onClick(item.href)
										setIsOpen(false)
									}}>
									<span className='text-14 font-[350] transition-colors duration-200'>{item.label}</span>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	)
}

const AnchorTabItem = ({
	label,
	href,
	active,
	onClick,
}: AnchorTabItemProps) => {
	return (
		<button
			className={cx(
				'text-14 font-[350] text-black capitalize leading-tight border border-transparent rounded-xs py-10 px-20',
				'transition-all duration-300 ease-in-out transform',
				active ? 'border border-dark scale-105' : 'hover:border-dark hover:scale-102',
			)}
			onClick={onClick}>
			{label}
		</button>
	)
}
